import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PersonaShell from './persona-shell';
import Spinner from '../../components/common/spinner';
import {
  PromptVersion,
  PromptVersionStatus,
  PromptFieldDiff,
  useActivatePromptVersion,
  useCreatePromptVersion,
  useDeletePromptVersion,
  useGeneratePromptVersion,
  usePersona,
  usePromptDiff,
  usePromptVersions,
  useUpdatePromptVersion,
} from '../../hooks/usePersonaAdmin';

const statusClass = (status: PromptVersionStatus) => {
  switch (status) {
    case 'active':
      return 'bg-green text-white';
    case 'draft':
      return 'bg-yellow-100 text-yellow-800';
    case 'archived':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const DiffView: React.FC<{ vid: number; against?: number }> = ({ vid, against }) => {
  const { data, isLoading } = usePromptDiff(vid, against);
  if (isLoading) return <Spinner alignmentClass="flex items-center justify-center py-6" />;
  if (!data || !data.fields || data.fields.length === 0)
    return <div className="text-xs text-gray-400">No differences found.</div>;

  return (
    <div className="space-y-3">
      {data.fields.map((fd: PromptFieldDiff) => (
        <div key={fd.field} className="rounded border border-gray-200">
          <div className="border-b bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-600">
            {fd.field}
          </div>
          <pre className="max-h-[30vh] overflow-auto rounded-b bg-gray-900 p-3 font-mono text-xs text-gray-100">
            {(fd.diff || '').split('\n').map((line, idx) => {
              let cls = 'text-gray-100';
              if (line.startsWith('+')) cls = 'text-green-300';
              else if (line.startsWith('-')) cls = 'text-red-300';
              else if (line.startsWith('@@')) cls = 'text-cyan-300';
              return (
                <div key={idx} className={cls}>
                  {line || ' '}
                </div>
              );
            })}
          </pre>
        </div>
      ))}
    </div>
  );
};

const PersonaPromptsPage: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const { data, isLoading } = usePromptVersions(personaId);
  const { data: persona } = usePersona(personaId);

  const createDraft = useCreatePromptVersion(personaId);
  const updateVersion = useUpdatePromptVersion(personaId);
  const activateVersion = useActivatePromptVersion(personaId);
  const generateVersion = useGeneratePromptVersion(personaId);
  const deleteVersion = useDeletePromptVersion(personaId);

  const versions = data?.data || [];
  const activeVersion = useMemo(() => versions.find((v) => v.status === 'active'), [versions]);
  const hasCorpus = (persona?.chunk_count ?? 0) > 0;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [profilePrompt, setProfilePrompt] = useState('');
  const [scopeLine, setScopeLine] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [showDiff, setShowDiff] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const [generateIncludeRules, setGenerateIncludeRules] = useState(true);
  const [generateActivate, setGenerateActivate] = useState(false);

  useEffect(() => {
    if (selectedId === null && versions.length > 0) setSelectedId(versions[0].id);
  }, [versions, selectedId]);

  const selected: PromptVersion | undefined = useMemo(
    () => versions.find((v) => v.id === selectedId),
    [versions, selectedId],
  );

  useEffect(() => {
    if (selected) {
      setProfilePrompt(selected.profile_prompt || '');
      setScopeLine(selected.scope_line || '');
      setWelcomeMessage(selected.welcome_message || '');
      setChangeNote(selected.change_note || '');
      setShowDiff(false);
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEditingDraft = selected?.status === 'draft';
  const isDirty = selected
    ? profilePrompt !== (selected.profile_prompt || '') ||
      scopeLine !== (selected.scope_line || '') ||
      welcomeMessage !== (selected.welcome_message || '') ||
      changeNote !== (selected.change_note || '')
    : false;

  const handleSaveDraft = async () => {
    if (!selected) return;
    if (isEditingDraft) {
      await updateVersion.mutateAsync({
        vid: selected.id,
        payload: {
          profile_prompt: profilePrompt,
          scope_line: scopeLine || undefined,
          welcome_message: welcomeMessage || undefined,
          change_note: changeNote || undefined,
        },
      });
    } else {
      const draft = await createDraft.mutateAsync({
        profile_prompt: profilePrompt,
        scope_line: scopeLine || undefined,
        welcome_message: welcomeMessage || undefined,
        change_note: changeNote || undefined,
        from_version_id: selected.id,
      });
      setSelectedId(draft.id);
    }
  };

  const handleNewDraft = async () => {
    const draft = await createDraft.mutateAsync({
      profile_prompt: profilePrompt || '',
      change_note: 'New draft',
      from_version_id: selected?.id,
    });
    setSelectedId(draft.id);
  };

  const handleActivate = async () => {
    if (!selected) return;
    await activateVersion.mutateAsync({ vid: selected.id, change_note: changeNote || undefined });
    setShowActivateConfirm(false);
  };

  const handleGenerate = async () => {
    const version = await generateVersion.mutateAsync({
      include_rules: generateIncludeRules,
      activate: generateActivate,
      change_note: changeNote.trim() || undefined,
    });
    setSelectedId(version.id);
    setShowGenerateConfirm(false);
  };

  const handleDelete = async () => {
    if (!selected || selected.status === 'active') return;
    if (!window.confirm(`Delete v${selected.version}? This cannot be undone.`)) return;
    const deletedId = selected.id;
    await deleteVersion.mutateAsync(deletedId);
    const remaining = versions.filter((v) => v.id !== deletedId);
    setSelectedId(remaining[0]?.id ?? null);
  };

  return (
    <PersonaShell personaId={personaId} tab="prompts">
      <div className="mx-auto max-w-6xl p-8">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Prompt Versions</h2>
            <p className="text-xs text-gray-500">
              Active version:{' '}
              <span className="font-mono">
                {activeVersion ? `v${activeVersion.version} (#${activeVersion.id})` : 'none'}
              </span>
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              onClick={() => setShowGenerateConfirm(true)}
              disabled={!hasCorpus || generateVersion.isLoading}
              title={hasCorpus ? 'Generate a draft from corpus (+ rules)' : 'Upload corpus on Overview first'}
            >
              Generate from corpus
            </button>
            <button
              className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={handleNewDraft}
              disabled={createDraft.isLoading}
            >
              {createDraft.isLoading ? 'Creating…' : 'New Draft'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-1">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Versions</h3>
            {isLoading && !data ? (
              <Spinner alignmentClass="flex items-center justify-center py-8" />
            ) : (
              <div className="max-h-[70vh] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm">
                {versions.length === 0 && (
                  <div className="p-4 text-center text-xs text-gray-400">No prompt versions yet.</div>
                )}
                {versions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => setSelectedId(version.id)}
                    className={`block w-full border-b px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                      selectedId === version.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">v{version.version}</span>
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(version.status)}`}
                      >
                        {version.status}
                      </span>
                    </div>
                    {version.change_note && (
                      <div className="mt-1 max-h-8 overflow-hidden text-xs text-gray-500">{version.change_note}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3 md:col-span-2">
            {selected ? (
              <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      v{selected.version} · <span className="font-mono text-xs text-gray-500">#{selected.id}</span>
                    </div>
                    <div className="text-xs text-gray-500">Status: {selected.status}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowDiff((s) => !s)}
                    >
                      {showDiff ? 'Hide diff' : 'Diff against active'}
                    </button>
                    {selected.status !== 'active' && (
                      <>
                        <button
                          className="bg-green hover:bg-green/80 rounded px-3 py-1.5 text-sm font-semibold text-black shadow-sm disabled:opacity-50"
                          onClick={() => setShowActivateConfirm(true)}
                          disabled={activateVersion.isLoading}
                        >
                          Activate…
                        </button>
                        <button
                          className="border-red text-red hover:bg-red rounded border px-2 py-1 text-xs font-medium hover:text-white disabled:opacity-50"
                          onClick={handleDelete}
                          disabled={deleteVersion.isLoading}
                        >
                          {deleteVersion.isLoading ? 'Deleting…' : 'Delete'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {showDiff && activeVersion && activeVersion.id !== selected.id && (
                  <div className="mb-4">
                    <DiffView vid={selected.id} against={activeVersion.id} />
                  </div>
                )}

                <label className="mb-1 block text-xs font-medium text-gray-600">Change note</label>
                <input
                  className="mb-3 w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  placeholder="Describe why this change is being made"
                />

                <label className="mb-1 block text-xs font-medium text-gray-600">Profile Prompt</label>
                <textarea
                  className="w-full rounded border border-gray-300 p-2 font-mono text-xs focus:ring-2 focus:ring-blue-500"
                  rows={14}
                  value={profilePrompt}
                  onChange={(e) => setProfilePrompt(e.target.value)}
                />

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Scope Line</label>
                    <input
                      className="w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
                      value={scopeLine}
                      onChange={(e) => setScopeLine(e.target.value)}
                      placeholder="One-line persona scope"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Welcome Message</label>
                    <input
                      className="w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
                      value={welcomeMessage}
                      onChange={(e) => setWelcomeMessage(e.target.value)}
                      placeholder="Welcome message for this version"
                    />
                  </div>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <button
                    className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    disabled={!isDirty || updateVersion.isLoading || createDraft.isLoading}
                    onClick={handleSaveDraft}
                  >
                    {isEditingDraft ? 'Save Draft' : 'Save as New Draft'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-xs text-gray-500">
                Select a version to view or edit it.
              </div>
            )}
          </div>
        </div>
      </div>

      {showGenerateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Generate from corpus?</h3>
            <p className="mb-4 text-sm text-gray-600">
              Uses GPT-4o to draft a profile prompt from this persona&apos;s corpus
              {persona?.chunk_count ? ` (${persona.chunk_count} chunks)` : ''}. This can take a minute.
            </p>
            <label className="mb-3 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={generateIncludeRules}
                onChange={(e) => setGenerateIncludeRules(e.target.checked)}
              />
              Include enabled rules as behavioral constraints
            </label>
            <label className="mb-4 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={generateActivate}
                onChange={(e) => setGenerateActivate(e.target.checked)}
              />
              Activate immediately after generating
            </label>
            <label className="mb-1 block text-xs font-medium text-gray-600">Change note (optional)</label>
            <input
              className="mb-4 w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="Auto-generated from corpus"
            />
            <div className="flex justify-end gap-2">
              <button
                className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setShowGenerateConfirm(false)}
                disabled={generateVersion.isLoading}
              >
                Cancel
              </button>
              <button
                className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={handleGenerate}
                disabled={generateVersion.isLoading}
              >
                {generateVersion.isLoading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showActivateConfirm && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">Activate v{selected.version}?</h3>
            <p className="mb-4 text-sm text-gray-600">
              Activating this prompt version will immediately replace the currently active version. All new chats with
              this Peersona will use it.
            </p>
            <label className="mb-1 block text-xs font-medium text-gray-600">Change note (recommended)</label>
            <input
              className="mb-4 w-full rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setShowActivateConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="bg-green hover:bg-green/80 rounded px-3 py-1.5 text-sm font-semibold text-black shadow-sm disabled:opacity-50"
                onClick={handleActivate}
                disabled={activateVersion.isLoading}
              >
                {activateVersion.isLoading ? 'Activating…' : 'Confirm & Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PersonaShell>
  );
};

export default PersonaPromptsPage;
